import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, InputAdornment, Skeleton, Snackbar, Stack, Table, TableBody,
  TableCell, TableContainer, TableFooter, TableHead, TableRow, TextField,
  Tooltip, Typography,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { BsPencil, BsPlusLg, BsTrash } from 'react-icons/bs';

import axiosInstance from '../../axiosInstance';
import CorrectTextModal from '../Report/CorrectTextModal';
import Fiche from '../Expert/Fiche';
import { euros, nombre } from '../Costings/constantes';
import { themeExpert } from '../../theme/expert';
import ChampDate from '../ChampDate/ChampDate';

const VIDE = {
  item: '', description: '', amount_ask: '', amount_buy: '', date_buy: '',
  amount_propose: '',
};

const dateCourte = (v) => (v ? new Date(v).toLocaleDateString('fr-BE') : null);

/** Le contenu : ce que l'assuré a perdu, et ce qui est retenu pour chaque objet. */
function ListContent() {
  const { expertiseId } = useParams();

  const [objets, setObjets] = useState([]);
  const [totaux, setTotaux] = useState({ total_ask: 0, total: 0 });
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [message, setMessage] = useState('');
  const [occupe, setOccupe] = useState(false);

  const [fiche, setFiche] = useState(null);
  const [aCorriger, setACorriger] = useState(null);
  const [aSupprimer, setASupprimer] = useState(null);
  const [recapitulatif, setRecapitulatif] = useState(false);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const [liste, somme] = await Promise.all([
        axiosInstance.get('/contents/', { params: { expertise_id: expertiseId } }),
        axiosInstance.get(`/totalcontent/${expertiseId}/`),
      ]);
      setObjets(liste.data.results || liste.data);
      setTotaux(somme.data);
      setErreur('');
    } catch {
      setErreur("Le contenu n'a pas pu être chargé.");
    } finally {
      setChargement(false);
    }
  }, [expertiseId]);

  useEffect(() => { charger(); }, [charger]);

  const poser = (champ) => (e) =>
    setFiche((etat) => ({ ...etat, [champ]: e.target.value }));

  const enregistrer = async (e) => {
    e.preventDefault();
    setOccupe(true);
    try {
      const corps = {
        ...fiche,
        date_buy: fiche.date_buy || null,
        amount_ask: nombre(fiche.amount_ask),
        amount_buy: nombre(fiche.amount_buy),
        amount_propose: nombre(fiche.amount_propose),
      };
      if (fiche.id) {
        await axiosInstance.put(`/contents/${fiche.id}/`, corps);
      } else {
        await axiosInstance.post('/contents/',
                                 { ...corps, total_content: totaux.id },
                                 { params: { expertise_id: expertiseId } });
      }
      setFiche(null);
      setMessage('Objet enregistré');
      charger();
    } catch {
      setErreur("L'objet n'a pas pu être enregistré.");
    } finally {
      setOccupe(false);
    }
  };

  const supprimer = async () => {
    setOccupe(true);
    try {
      await axiosInstance.delete(`/contents/${aSupprimer.id}/`);
      setASupprimer(null);
      setMessage('Objet supprimé');
      // Le total est recalculé par le serveur : on relit tout plutôt que de
      // retirer la ligne localement et garder un total périmé.
      charger();
    } catch {
      setErreur("L'objet n'a pas pu être supprimé.");
    } finally {
      setOccupe(false);
    }
  };

  const ecart = nombre(totaux.total_ask) - nombre(totaux.total);

  if (chargement) {
    return <Stack spacing={2}>
      <Skeleton height={110} /><Skeleton height={240} />
    </Stack>;
  }

  return (
    <>
      <Stack spacing={3}>
        {erreur && <Alert severity="error"
                          onClose={() => setErreur('')}>{erreur}</Alert>}

        <Fiche titre="Totaux du contenu">
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={4}>
            <Box>
              <Typography variant="caption" color="text.secondary"
                          component="div">
                Réclamé par l'assuré
              </Typography>
              <Typography variant="h2">{euros(totaux.total_ask)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary"
                          component="div">
                Retenu
              </Typography>
              <Typography variant="h2">{euros(totaux.total)}</Typography>
            </Box>
            {ecart !== 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary"
                            component="div">
                  Écart
                </Typography>
                <Typography variant="h2"
                            color={ecart > 0 ? 'warning.main' : 'success.main'}>
                  {euros(Math.abs(ecart))}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {ecart > 0 ? 'de moins que réclamé' : 'de plus que réclamé'}
                </Typography>
              </Box>
            )}
          </Stack>
        </Fiche>

        <Fiche
          titre={`Objets (${objets.length})`}
          explication="Un objet par ligne, avec sa valeur d'achat si elle est justifiée."
          actions={(
            <Stack direction="row" spacing={1.5}>
              <Button size="small" variant="outlined"
                      onClick={() => setRecapitulatif(true)}
                      disabled={objets.length === 0}>
                Récapitulatif
              </Button>
              <Button size="small" variant="contained" startIcon={<BsPlusLg />}
                      onClick={() => setFiche({ ...VIDE })}>
                Ajouter un objet
              </Button>
            </Stack>
          )}
          sx={{ p: 0 }}
        >
          {objets.length === 0 ? (
            <Typography variant="body2" color="text.secondary"
                        sx={{ py: 4, textAlign: 'center' }}>
              Aucun objet. Commencez par en ajouter un.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Objet</TableCell>
                    <TableCell align="right">Réclamé</TableCell>
                    <TableCell align="right">Valeur d'achat</TableCell>
                    <TableCell>Acheté le</TableCell>
                    <TableCell align="right">Retenu</TableCell>
                    <TableCell align="right">&nbsp;</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {objets.map((objet) => (
                    <TableRow key={objet.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {objet.item}
                        </Typography>
                        {objet.description && (
                          <Typography variant="caption" color="text.secondary">
                            {objet.description}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {euros(objet.amount_ask)}
                      </TableCell>
                      <TableCell align="right">
                        {euros(objet.amount_buy)}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {dateCourte(objet.date_buy)
                          || <Box component="span"
                                  sx={{ color: 'text.disabled' }}>—</Box>}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {euros(objet.amount_propose)}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title="Faire relire l'objet et sa description">
                          <IconButton size="small"
                                      onClick={() => setACorriger(objet)}>
                            ✨
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Modifier">
                          <IconButton size="small"
                                      onClick={() => setFiche({
                                        ...VIDE, ...objet,
                                        date_buy: (objet.date_buy || '')
                                          .split('T')[0] })}>
                            <BsPencil />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Supprimer">
                          <IconButton size="small"
                                      onClick={() => setASupprimer(objet)}>
                            <BsTrash />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700,
                                     color: 'text.primary' }}>Total</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700,
                                                   color: 'text.primary' }}>
                      {euros(totaux.total_ask)}
                    </TableCell>
                    <TableCell />
                    <TableCell />
                    <TableCell align="right" sx={{ fontWeight: 700,
                                                   color: 'text.primary' }}>
                      {euros(totaux.total)}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          )}
        </Fiche>
      </Stack>

      <CorrectTextModal
        show={Boolean(aCorriger)}
        onHide={() => setACorriger(null)}
        correctUrl={aCorriger ? `/contents/${aCorriger.id}/correct/` : ''}
        fields={[
          { key: 'item', label: 'Objet', value: aCorriger?.item || '',
            plainText: true, spellingOnly: true },
          { key: 'description', label: 'Description',
            value: aCorriger?.description || '', plainText: true },
        ]}
        onSave={async (valeurs) => {
          await axiosInstance.put(`/contents/${aCorriger.id}/`,
                                  { ...aCorriger, ...valeurs });
          setACorriger(null);
          setMessage('Correction enregistrée');
          charger();
        }}
      />

      <ThemeProvider theme={themeExpert}>
        <Dialog open={fiche !== null} onClose={() => setFiche(null)}
                fullWidth maxWidth="sm">
          <Box component="form" onSubmit={enregistrer} noValidate>
            <DialogTitle>
              {fiche?.id ? "Modifier l'objet" : 'Ajouter un objet'}
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2.5}>
                <TextField label="Objet" value={fiche?.item || ''}
                           onChange={poser('item')} required autoFocus
                           helperText="Ex. : canapé trois places, en tissu." />

                <TextField label="Description" multiline minRows={2}
                           value={fiche?.description || ''}
                           onChange={poser('description')}
                           helperText="Marque, modèle, état — ce qui justifie la valeur." />

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField label="Montant réclamé" type="number"
                             inputProps={{ step: '0.01' }}
                             value={fiche?.amount_ask ?? ''}
                             onChange={poser('amount_ask')}
                             InputProps={{ startAdornment: (
                               <InputAdornment position="start">€</InputAdornment>) }}
                             helperText="Ce que l'assuré demande." />
                  <TextField label="Valeur d'achat" type="number"
                             inputProps={{ step: '0.01' }}
                             value={fiche?.amount_buy ?? ''}
                             onChange={poser('amount_buy')}
                             InputProps={{ startAdornment: (
                               <InputAdornment position="start">€</InputAdornment>) }}
                             helperText="Selon facture ou preuve d'achat." />
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <ChampDate label="Date d'achat"
                             value={fiche?.date_buy || ''}
                             onChange={poser('date_buy')}
                             InputLabelProps={{ shrink: true }}
                             helperText="Elle détermine la vétusté." />
                  <TextField label="Montant retenu" type="number"
                             inputProps={{ step: '0.01' }}
                             value={fiche?.amount_propose ?? ''}
                             onChange={poser('amount_propose')}
                             InputProps={{ startAdornment: (
                               <InputAdornment position="start">€</InputAdornment>) }}
                             helperText="Ce que vous retenez pour cet objet." />
                </Stack>
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={() => setFiche(null)} disabled={occupe}>
                Annuler
              </Button>
              <Button type="submit" variant="contained"
                      disabled={occupe || !fiche?.item?.trim()}>
                {occupe ? 'Enregistrement…' : 'Enregistrer'}
              </Button>
            </DialogActions>
          </Box>
        </Dialog>

        <Dialog open={recapitulatif} onClose={() => setRecapitulatif(false)}
                fullWidth maxWidth="md" scroll="paper">
          <DialogTitle>Récapitulatif du contenu</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2}>
              {objets.map((objet) => (
                <Box key={objet.id} sx={{ pb: 2, borderBottom: '1px solid',
                                          borderColor: 'divider' }}>
                  <Typography variant="h3">{objet.item}</Typography>
                  {objet.description && (
                    <Typography variant="body2" color="text.secondary"
                                sx={{ mb: 1 }}>
                      {objet.description}
                    </Typography>
                  )}
                  <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                    <Box>
                      <Typography variant="caption" color="text.secondary"
                                  component="div">Réclamé</Typography>
                      <Typography variant="body2">
                        {euros(objet.amount_ask)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary"
                                  component="div">Valeur d'achat</Typography>
                      <Typography variant="body2">
                        {euros(objet.amount_buy)}
                        {dateCourte(objet.date_buy)
                          && ` — le ${dateCourte(objet.date_buy)}`}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary"
                                  component="div">Retenu</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {euros(objet.amount_propose)}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              ))}
              <Stack direction="row" spacing={4}>
                <Box>
                  <Typography variant="caption" color="text.secondary"
                              component="div">Total réclamé</Typography>
                  <Typography variant="h2">{euros(totaux.total_ask)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary"
                              component="div">Total retenu</Typography>
                  <Typography variant="h2">{euros(totaux.total)}</Typography>
                </Box>
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setRecapitulatif(false)}>Fermer</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={aSupprimer !== null} onClose={() => setASupprimer(null)}>
          <DialogTitle>Supprimer cet objet ?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              « {aSupprimer?.item} » sera retiré du contenu, et les totaux
              recalculés. C'est définitif.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setASupprimer(null)} disabled={occupe}>
              Annuler
            </Button>
            <Button onClick={supprimer} color="error" variant="contained"
                    disabled={occupe}>
              Supprimer
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>

      <Snackbar open={Boolean(message)} autoHideDuration={3000}
                onClose={() => setMessage('')} message={message}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </>
  );
}

export default ListContent;
